import { FC, useEffect, useRef, useState } from 'react';
import LogoUpload from '../../LogoUpload';
import NextImage from 'next/image';
import ImageWithFallback from '../../../common/ImageWithFallback';
import Label from '../../../chat/Label';
import SubLabel from '../../../chat/SubLabel';
import {
  StyleImageResetModalWrap,
  StyledImageIconWrap,
  StyledLogoSettingWrapper,
  StyledTitleWrap
} from './Styled';
import InfoButton from '../../../chat/Button/InfoButton';
import Clip from '../../../icons/Clip';
import formatBytes from '../../../../utils/formatBytes';
import { ErrorText } from '../../../../styles/chime-react/CommonStyles';
import { useNavigationGuard } from '../../../../hooks/useNavigationGuard';
import { Errors, IOS_CANVAS_SIZE_LIMIT } from '../../../../constants';
import { useAutoModal } from '../../../common/AutoModal/Context';
import blankCompany from '../../../../public/blank-company.png';
import blankProfile from '../../../../public/blank-profile.png';
import PrimaryButton from '../../../chat/Button/PrimaryButton';
import Heading from '../../../chat/Heading';
import { useMember } from '../../../../providers/SessionProvider';

const LogoSetting: FC<{
  url?: string;
  fallback?: Parameters<typeof NextImage>[0]['src'];
  onChange: (data?: Blob) => void;
  onResetLogo?: (() => Promise<void>) | false;
  displayLarge?: boolean;
  showResetLink?: boolean;
  shouldClearData?: boolean;
  forStaff?: boolean;
  title?: string;
  useFor?: string;
}> = ({
  url,
  fallback,
  onChange,
  onResetLogo,
  displayLarge,
  showResetLink = true,
  shouldClearData = false,
  forStaff = false,
  title,
  useFor = 'エンドユーザー'
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const eventBus = useAutoModal();
  const [avatar, setAvatar] = useState<Blob>();
  const [imgSrc, setImgSrc] = useState('');
  const [imgInfo, setImgInfo] = useState('');
  const [uploadError, setUploadError] = useState('');
  const { member } = useMember();
  const loadImage = (blob: Blob) => {
    setAvatar(blob);
    onChange(blob);
  };

  useEffect(() => {
    if (shouldClearData) {
      clearImage();
    }
  }, [shouldClearData]);

  const clearImage = () => {
    setImgSrc('');
    setImgInfo('');
    fileRef.current!.value = '';
    setAvatar(undefined);
    onChange(undefined);
    setUploadError('');
  };

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (
        !['jpeg', 'jpg', 'png'].reduce(
          (prev, type) => prev || !!file?.type.toLowerCase().includes(type),
          false
        )
      ) {
        setUploadError(
          'アップロードできるファイルの拡張子はjpeg,pngのみとなります。'
        );
      } else if (file.size / 1024 / 1024 >= 5) {
        setUploadError('画像のサイズを5MB以内としてください。');
      } else {
        const reader = new FileReader();
        reader.addEventListener('load', () => {
          const tempImg = new Image();
          tempImg.src = reader.result?.toString() || '';
          tempImg.onload = () => {
            if (tempImg.width * tempImg.height > IOS_CANVAS_SIZE_LIMIT) {
              setUploadError(
                '画像サイズ（寸法）が大きすぎます。画像の寸法を小さくするか、別の画像を選択してください。'
              );
            } else {
              setUploadError('');
              setImgSrc(reader.result?.toString() || '');
              setImgInfo(
                `${e.target.files![0].name}（${formatBytes(
                  e.target.files![0].size
                )}）`
              );
            }
          };
        });
        reader.readAsDataURL(e.target.files[0]);
      }
    }
  };

  useNavigationGuard([imgSrc, avatar], Errors.ImageChanged);

  return (
    <StyledLogoSettingWrapper displayLarge={displayLarge}>
      <StyledTitleWrap>
        <div className="title-text-wrap">
          <Label tag="p">{title || 'アイコン設定'}</Label>
          {!forStaff && <SubLabel>※{useFor}に表示されるアイコンです</SubLabel>}
          <SubLabel>※推奨サイズ：縦200px・横200px、5MB以内</SubLabel>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={onSelectFile}
          hidden
        />
        <div className="upload-info-wrap">
          <div className="button-wrap">
            <InfoButton
              className="image-select-button"
              label="画像を選択"
              size="sm"
              outline={!!displayLarge}
              icon={
                <Clip
                  width={displayLarge ? '16px' : '10.3px'}
                  height={displayLarge ? '18.1px' : '11.7px'}
                />
              }
              iconSize="auto"
              onClick={() => fileRef.current?.click()}
            />
            {imgInfo && <span className="image-info">{imgInfo}</span>}
          </div>
          {uploadError && (
            <ErrorText className="error-text">{uploadError}</ErrorText>
          )}
          {showResetLink && (
            <div className="image-reset-wrapper">
              <a
                className="image-reset-link"
                href=""
                onClick={(e) => {
                  e.preventDefault();
                  eventBus?.emit('open', {
                    component: onResetLogo ? (
                      <>
                        <StyleImageResetModalWrap>
                          <Heading level={6} tag="h2" marginBottom="1rem">
                            登録しているアイコンを削除しますか？
                          </Heading>
                          ※ デフォルトアイコンに変更
                          <div className="image-wrapper">
                            <NextImage
                              src={
                                forStaff ? blankProfile.src : blankCompany.src
                              }
                              width={120}
                              height={120}
                              alt="デフォルトアイコン"
                              style={{ borderRadius: 60 }}
                            ></NextImage>
                          </div>
                          <SubLabel>デフォルトアイコン</SubLabel>
                          <PrimaryButton
                            className="button"
                            label="デフォルトアイコンに変更して保存する"
                            onClick={() => {
                              onResetLogo().finally(() => {
                                eventBus?.emit('close');
                              });
                            }}
                          />
                        </StyleImageResetModalWrap>
                      </>
                    ) : (
                      <>
                        <StyleImageResetModalWrap noImage>
                          デフォルト画像が設定されています。
                          <PrimaryButton
                            className="button"
                            label="閉じる"
                            onClick={() => {
                              eventBus?.emit('close');
                            }}
                          />
                        </StyleImageResetModalWrap>
                      </>
                    )
                  });
                }}
              >
                登録しているアイコンを削除する
              </a>
              <SubLabel>※デフォルト画像に変更になります。</SubLabel>
            </div>
          )}
        </div>
      </StyledTitleWrap>
      <div>
        {imgSrc || displayLarge ? (
          <LogoUpload
            onChange={loadImage}
            onCancel={clearImage}
            imgSrc={imgSrc}
            displayLarge={displayLarge}
          />
        ) : (
          <StyledImageIconWrap>
            {url && (
              <ImageWithFallback
                src={url!}
                fallback={fallback}
                width="52"
                height="52"
                alt="アイコン画像"
              />
            )}
          </StyledImageIconWrap>
        )}
      </div>
    </StyledLogoSettingWrapper>
  );
};

export default LogoSetting;
